import {
    faBadgePercent,
    faMedal,
    faEnvelope,
    faSms,
    faStar,
    faUserCheck,
    faDiagramSankey
} from '@fortawesome/pro-regular-svg-icons';
import Loyalty from 'components/Marketing/Loyalty';
import PageSidebar, { PageSidebarItem } from 'components/PageSidebar';
import SMSDashboard from 'components/SMS/SMSCampaigns';
import LeadsComponent from 'components/Leads';
import AutomationsComponent from 'components/Automations';
import { Fragment, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PrivatePage from './PrivatePage';
import Reviews from 'components/Marketing/Reviews';
import EmailMarketingComponent from 'components/EmailMarketing/EmailMarketingComponent';
import PromoCodesComponent from 'components/PromoCodes/PromoCodesComponent';
import { PromoCodesProvider } from 'components/PromoCodes/PromoCodesProvider';
import {
    MarketingAutomationsPath,
    MarketingEmailsPath,
    MarketingPath,
    MarketingPromoCodes,
    MarketingReviewsPath,
    MarketingLeadsPath,
    MarketingLoyaltyPath
} from 'routes/Paths';
import { useMarketplace } from 'hooks';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';

const enum ShowTypes {
    SMSCampaigns = 'SMS',
    Reviews = 'REVIEWS',
    Marketplace = 'MARKET',
    PromoCodes = 'PROMO_CODES',
    Leads = 'LEADS',
    Loyalty = 'LOYALTY',
    Automations = 'AUTOMATIONS',
    Emails = 'EMAILS'
}

/**
 * Marketing page for SMS Campaigns & Marketplace
 */
const MarketingPage = () => {
    const history = useHistory();
    const location = useLocation();

    // Leads activation now lives on the Leads page itself, so the nav entry must be
    // reachable whenever the feature flag is on (premium businesses can activate it
    // in-page; non-premium businesses get the premium upsell on toggle).
    const leadsVisible = process.env.REACT_APP_LEADS_ENABLED === 'true';

    // Determine current show state based on current route
    const getCurrentShow = (): string => {
        if (location.pathname === MarketingPromoCodes) {
            return ShowTypes.PromoCodes;
        }
        if (location.pathname === MarketingLeadsPath) {
            return ShowTypes.Leads;
        }
        if (location.pathname === MarketingLoyaltyPath) {
            return ShowTypes.Loyalty;
        }
        if (location.pathname === MarketingAutomationsPath) {
            return ShowTypes.Automations;
        }
        if (location.pathname === MarketingEmailsPath) {
            return ShowTypes.Emails;
        }
        if (location.pathname === MarketingReviewsPath) {
            return ShowTypes.Reviews;
        }
        return ShowTypes.SMSCampaigns;
    };

    const [show, setShow] = useState<string>(getCurrentShow());

    // Update show state when route changes
    useEffect(() => {
        setShow(getCurrentShow());
    }, [location.pathname]);

    useEffect(() => {
        if (!leadsVisible && show === ShowTypes.Leads) {
            setShow(ShowTypes.SMSCampaigns);
            history.replace(MarketingPath);
        }
    }, [leadsVisible, show]);

    const pageSidebarItems: Array<PageSidebarItem> = [
        { icon: faSms, title: 'SMS Campaigns', key: ShowTypes.SMSCampaigns },
        ...(leadsVisible ? [{ icon: faUserCheck, title: 'Leads', key: ShowTypes.Leads }] : []),
        { icon: faStar, title: 'Reviews', key: ShowTypes.Reviews },
        // { icon: faStore, title: "Marketplace", key: ShowTypes.Marketplace },
        { icon: faMedal, title: 'Loyalty', key: ShowTypes.Loyalty },
        { icon: faBadgePercent, title: 'Promo Codes', key: ShowTypes.PromoCodes },
        { icon: faDiagramSankey, title: 'Automations', key: ShowTypes.Automations },
        { icon: faEnvelope, title: 'Email Marketing', key: ShowTypes.Emails }
    ];

    const handleSidebarSelect = (key: string) => {
        setShow(key);

        // Navigate to the appropriate route
        if (key === ShowTypes.PromoCodes) {
            history.push(MarketingPromoCodes);
        } else if (key === ShowTypes.Leads) {
            history.push(MarketingLeadsPath);
        } else if (key === ShowTypes.SMSCampaigns) {
            history.push(MarketingPath);
        } else if (key === ShowTypes.Reviews) {
            history.push(MarketingReviewsPath);
        } else if (key === ShowTypes.Loyalty) {
            history.push(MarketingLoyaltyPath);
        } else if (key === ShowTypes.Automations) {
            history.push(MarketingAutomationsPath);
        } else if (key === ShowTypes.Emails) {
            history.push(MarketingEmailsPath);
        }
    };

    const sidebar = (
        <PageSidebar
            title="Marketing"
            items={pageSidebarItems}
            selectedKey={show}
            onSelect={handleSidebarSelect}
        />
    );

    return (
        <Fragment>
            {show === ShowTypes.SMSCampaigns && (
                <PrivatePage
                    pageSidebar={sidebar}
                    title="SMS Campaigns - Cuddles"
                    pageName={MarketingPath}
                >
                    <SMSDashboard />
                </PrivatePage>
            )}

            {show === ShowTypes.Leads && (
                <PrivatePage
                    pageSidebar={sidebar}
                    title="Leads - Cuddles"
                    pageName={MarketingLeadsPath}
                >
                    <LeadsComponent />
                </PrivatePage>
            )}

            {show === ShowTypes.PromoCodes && (
                <PrivatePage
                    pageSidebar={sidebar}
                    title="Promo Codes - Cuddles"
                    pageName={MarketingPromoCodes}
                >
                    <PromoCodesProvider>
                        <PromoCodesComponent />
                    </PromoCodesProvider>
                </PrivatePage>
            )}

            {show === ShowTypes.Loyalty && (
                <PrivatePage
                    pageSidebar={sidebar}
                    title="Loyalty - Cuddles"
                    pageName={MarketingLoyaltyPath}
                >
                    <Loyalty />
                </PrivatePage>
            )}

            {show === ShowTypes.Marketplace && <PrivatePage pageSidebar={sidebar}></PrivatePage>}

            {show === ShowTypes.Automations && (
                <PrivatePage
                    pageSidebar={sidebar}
                    title="Automations - Cuddles"
                    pageName={MarketingAutomationsPath}
                >
                    <AutomationsComponent />
                </PrivatePage>
            )}
            {show === ShowTypes.Reviews && (
                <PrivatePage pageSidebar={sidebar}>
                    <Reviews />
                </PrivatePage>
            )}

            {show === ShowTypes.Emails && (
                <PrivatePage pageSidebar={sidebar}>
                    <EmailMarketingComponent />
                </PrivatePage>
            )}
        </Fragment>
    );
};

export default MarketingPage;
